import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, inject, signal, viewChild } from '@angular/core';
import { FormControl, NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { TenantUsersDto } from '@buspro/web/apis';
import {
  ErrorDisplayComponent,
  ExtendedProblemDetail,
  syncAutofilledInputs,
  useFormErrorHandler,
} from '@buspro/web/core';
import { NgxAuthService } from '@corelabs/ng-auth';
import { injectMutation } from '@tanstack/angular-query-experimental';
import { NzSpinComponent } from 'ng-zorro-antd/spin';
import { NGXLogger } from 'ngx-logger';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'tenant-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  standalone: true,
  imports: [CommonModule, NzSpinComponent, ReactiveFormsModule, ErrorDisplayComponent],
})
export class LoginComponent implements AfterViewInit {
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly logger = inject(NGXLogger);
  private readonly router = inject(Router);
  private readonly ngxAuthService = inject(NgxAuthService);
  private readonly formErrorHandler = useFormErrorHandler();

  // Form setup
  email = new FormControl<string>('', [Validators.required, Validators.email]);
  password = new FormControl<string>('', [Validators.required]);
  form = this.fb.group({
    email: this.email,
    password: this.password,
  });

  // Error state
  showError = signal(false);
  errorDetails: ExtendedProblemDetail | null = null;

  // Password visibility toggle
  isPasswordVisible = signal(false);

  private emailInputRef = viewChild<ElementRef<HTMLInputElement>>('emailInput');
  private loginFormRef = viewChild<ElementRef<HTMLFormElement>>('loginForm');
  isNavigating = false;

  // Login mutation using TanStack Query
  loginMutation = injectMutation(() => ({
    mutationFn: async (loginData: { email: string; password: string }): Promise<TenantUsersDto> => {
      // Convert Observable to Promise for TanStack Query compatibility
      return firstValueFrom(this.ngxAuthService.login(loginData));
    },
    onSuccess: (user: TenantUsersDto) => {
      this.logger.log('User logged in successfully', user);
      this.clearErrors();
      // Navigate to dashboard
      const homeRoute = '/dashboard';
      this.logger.log('Navigating user to', homeRoute);
      this.isNavigating = true;
      this.router.resetConfig(this.router.config);
      this.router
        .navigateByUrl(homeRoute)
        .catch((reason) => {
          this.logger.error('Navigation error:', reason);
        })
        .finally(() => {
          this.isNavigating = false;
        });
    },
    onError: (error: any) => {
      this.logger.error('Login failed', error);

      const result = this.formErrorHandler.handleFormError(error, {
        formName: 'tenant-login-form',
      });

      this.logger.debug('error result', result);

      this.showError.set(true);
      this.errorDetails = result.problemDetail;

      this.logger.debug('error details', this.errorDetails);
    },
  }));

  login() {
    // Clear previous errors
    this.clearErrors();

    // Adopt anything the browser autofilled. Chrome assigns input.value
    // directly, which Angular never sees, so without this the controls can
    // still be empty while the fields visibly hold the saved credentials —
    // and the user would be told both are required.
    syncAutofilledInputs(this.form, this.loginFormRef()?.nativeElement);

    // Validate form
    if (this.form.invalid) {
      this.markFormGroupTouched();
      return;
    }

    // Execute login mutation
    const loginData = {
      email: this.email.value || '',
      password: this.password.value || '',
    };

    this.loginMutation.mutate(loginData);
  }

  onRetryLogin() {
    this.clearErrors();
    this.login();
  }

  onDismissError() {
    this.clearErrors();
  }

  private markFormGroupTouched() {
    Object.keys(this.form.controls).forEach((key) => {
      const control = this.form.get(key);
      if (control) {
        control.markAsTouched();
      }
    });
  }

  ngAfterViewInit(): void {
    this.emailInputRef()?.nativeElement?.focus();
    // Page-load autofill lands after the first render, so give the browser a
    // tick and then adopt it. This is what makes the form report itself valid
    // without the user having to click into it first.
    setTimeout(() => {
      syncAutofilledInputs(this.form, this.loginFormRef()?.nativeElement);
    });
  }

  private clearErrors() {
    this.showError.set(false);
    this.errorDetails = null;
  }

  // Getter for backward compatibility with template
  get isSubmitting(): boolean {
    return this.loginMutation.isPending();
  }

  get isLoading(): boolean {
    return this.isSubmitting || this.isNavigating;
  }

  get spinnerMessage(): string {
    if (this.isSubmitting) return 'Authenticating...';
    if (this.isNavigating) return 'Redirecting...';
    return '';
  }
}
